/**
 * NotesLog — generic append-only progress log for a single work item.
 *
 * This is the shared engine behind BOTH the client-file notes log (FileNotes)
 * and the preparation-request notes log (PrepNotes). It renders a chronological
 * journal of human-written and system-generated notes (oldest at top, newest at
 * bottom, auto-scrolls to the latest entry) and a multi-line composer capped
 * via the `maxChars` prop (200 for both file notes and prep notes, matching
 * each table's DB CHECK — migrations 21 / 22), plus the §31 acknowledgement
 * affordance per note.
 *
 * Notes wrap: the composer is a textarea that grows in whole 32px ruled-line
 * steps (max three lines, then internal scroll; Enter saves, Shift+Enter
 * inserts a line break), and saved entries render `pre-wrap` across as many
 * ruled lines as they need. Every row is a whole multiple of 32px so the
 * paper's rule lines stay aligned down the entire column (CLAUDE.md §18).
 *
 * Notes cannot be edited or deleted from the UI — a FAIS audit requirement.
 * Corrections are made by adding a follow-up note.
 *
 * The parent supplies an `api` adapter so this component is storage-agnostic:
 *   api.getNotes(itemId)                     → Promise<note[]>
 *   api.createNote({ itemId, authorType, authorId, authorDisplayName, body,
 *                    authorRole })           → Promise<note>
 *   api.acknowledgeNote(noteId)             → Promise<note|null>
 *
 * Visually styled as an aged ruled-paper notebook: warm cream background,
 * horizontal rule lines every 32px, a margin line at left, a decorative coffee
 * stain in the bottom-right corner, and Caveat handwriting for note bodies.
 */

import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { getRoleHolderName } from '../../auth/authConfig';
import coffeeStainUrl from '../../assets/coffee_stain.png';

// Default cap. Both notes tables enforce a 200-char DB CHECK (file_notes:
// migration 21; prep_request_notes: migration 29); FileNotes and PrepNotes
// each pass their API's NOTE_BODY_MAX via the maxChars prop so the cap stays
// declared next to its DB contract.
export const NOTE_BODY_MAX = 200;

// One ruled line of the notepad, in px. The paper gradient, saved-entry
// line-height, and the composer's grow steps all share this rhythm.
const LINE_PX = 32;
// The composer grows to at most this many ruled lines, then scrolls.
const COMPOSER_MAX_LINES = 3;

function formatNoteDateShort(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
}

function formatNoteTimeShort(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export default function NotesLog({
  itemId,
  api,
  title = 'Notes',
  currentUser,
  currentRole = null,
  refreshSignal = 0,
  maxChars = NOTE_BODY_MAX,
}) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [ackingId, setAckingId] = useState(null);
  const scrollRef = useRef(null);
  const draftRef = useRef(null);

  const load = useCallback(async () => {
    if (!itemId) return;
    try {
      const rows = await api.getNotes(itemId);
      setNotes(rows);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, itemId]);

  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  // Auto-scroll to the bottom whenever the notes list changes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [notes]);

  // Auto-grow the composer in whole ruled-line (32px) steps: one line when
  // empty, up to three lines, then it stops growing and scrolls internally.
  // Height is re-derived from scrollHeight on every draft change, so the box
  // also shrinks back as text is deleted. Layout effect so the height is set
  // before paint (no one-frame flash of the wrong height).
  useLayoutEffect(() => {
    const el = draftRef.current;
    if (!el) return;
    el.style.height = `${LINE_PX}px`;
    const lines = Math.max(1, Math.round(el.scrollHeight / LINE_PX));
    el.style.height = `${Math.min(lines, COMPOSER_MAX_LINES) * LINE_PX}px`;
    el.style.overflowY = lines > COMPOSER_MAX_LINES ? 'auto' : 'hidden';
  }, [draft]);

  const charCount = draft.length;
  const overLimit = charCount > maxChars;
  // Muted until the note passes 3/4 of the cap (150 of 200), then Optimum
  // orange; danger red only once actually over the cap.
  const counterColour =
    overLimit ? 'var(--cp-danger)'
    : charCount > Math.round(maxChars * 0.75) ? '#F2682A'
    : 'var(--cp-text-muted)';

  const submit = async () => {
    const body = draft.trim();
    if (!body || overLimit || submitting) return;
    if (!currentUser?.displayName) {
      setError('Cannot identify current user.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const inserted = await api.createNote({
        itemId,
        authorType: 'user',
        authorId: currentUser.id || null,
        authorDisplayName: currentUser.displayName,
        body,
        // The active role decides who must acknowledge this note (§31).
        authorRole: currentRole,
      });
      setNotes((prev) => [...prev, inserted]);
      setDraft('');
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Acknowledge a note (owner-only, gated in EntryRow). Optimistically marks
  // the note read locally so the row updates immediately, then reconciles with
  // the RPC result; reverts on error (CLAUDE.md §31).
  const acknowledge = useCallback(async (noteId) => {
    if (ackingId) return;
    setAckingId(noteId);
    setError(null);
    const snapshot = notes;
    setNotes((prev) =>
      prev.map((n) =>
        n.id === noteId ? { ...n, acknowledgedAt: new Date().toISOString() } : n
      )
    );
    try {
      const updated = await api.acknowledgeNote(noteId);
      if (updated) {
        setNotes((prev) => prev.map((n) => (n.id === noteId ? updated : n)));
      }
    } catch (e) {
      setNotes(snapshot); // revert the optimistic tick
      setError(e.message);
    } finally {
      setAckingId(null);
    }
  }, [api, ackingId, notes]);

  const onKeyDown = (e) => {
    // Enter saves the note; Shift+Enter inserts a hard line break. The
    // preventDefault on bare Enter stops the textarea from also inserting a
    // newline as it submits.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>

      {/* Entries panel — paper */}
      <div style={styles.notepad}>
        <div style={styles.marginLine} />
        <img src={coffeeStainUrl} alt="" style={styles.coffeeStain} />
        <div ref={scrollRef} style={styles.scroll}>
          {loading && <PlaceholderRow text="Loading…" />}
          {!loading && notes.length === 0 && (
            <PlaceholderRow text="No notes yet. Add the first one below." />
          )}
          {notes.map((note) => (
            <EntryRow
              key={note.id}
              note={note}
              currentRole={currentRole}
              onAcknowledge={acknowledge}
              acking={ackingId === note.id}
            />
          ))}
        </div>
      </div>

      {/* Add-note panel — paper. Mirrors the entries panel: the outer
          wrapper carries chrome (border + radius + paper colour), and an
          inner child carries the rules (gradient + padding). */}
      <div style={styles.inputPanel}>
        <div style={styles.marginLine} />
        <div style={styles.inputRuled}>
          <div style={styles.inputRow}>
            <div style={styles.metaCol}>
              <span style={styles.newEntryLabel}>New entry</span>
            </div>
            <div style={styles.noteColInput}>
              <textarea
                ref={draftRef}
                className="inbox-note-textarea"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Add a note…"
                maxLength={maxChars + 20}
                rows={1}
                style={styles.textarea}
                disabled={submitting}
                aria-label="Add a note"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Hint + counter + submit row, sits OUTSIDE the input panel.
          Hint on the left; counter (hidden until typing starts) and the
          submit button on the right. */}
      <div style={styles.inputFooter}>
        <span style={styles.hint}>Enter to save · Shift+Enter for a new line</span>
        <div style={styles.footerRight}>
          {charCount > 0 && (
            <span style={{ ...styles.counter, color: counterColour }}>
              {charCount}/{maxChars}
            </span>
          )}
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={!draft.trim() || overLimit || submitting}
            style={styles.addBtn}
          >
            {submitting ? 'Adding…' : 'Add Note'}
          </button>
        </div>
      </div>
      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
}

function EntryRow({ note, currentRole, onAcknowledge, acking }) {
  const isSystem = note.authorType === 'system';
  // Only human notes flagged at write time carry an acknowledgement (§31).
  const requiresAck = note.requiresAck && !isSystem;
  const pending = requiresAck && !note.acknowledgedAt;
  const isOwner = requiresAck && currentRole && currentRole === note.acknowledgerRole;
  // Display name of the person holding the acknowledger role (falls back to
  // the role label — there is no profiles table).
  const ackLabel = getRoleHolderName(note.acknowledgerRole);

  const row = (
    <div style={styles.entry}>
      <div style={styles.metaCol}>
        <div style={styles.metaInner}>
          <div style={styles.metaTop}>{formatNoteDateShort(note.createdAt)}</div>
          <div style={styles.metaBottom}>
            {formatNoteTimeShort(note.createdAt)} — {note.authorDisplayName}
          </div>
        </div>
      </div>
      <div style={styles.noteCol}>
        <span style={isSystem ? styles.bodySystem : styles.bodyHuman}>
          {note.body}
        </span>
      </div>
    </div>
  );

  if (!requiresAck) return row;

  // Acknowledgement-bearing note: while pending it is highlighted amber for
  // ALL viewers; the status strip below shows a checkbox (owner) or a passive
  // badge (everyone else). Once acknowledged the highlight is removed.
  return (
    <div style={pending ? { ...styles.ackWrap, ...styles.ackWrapPending } : styles.ackWrap}>
      {row}
      <div style={styles.ackStatusRow}>
        {pending && isOwner ? (
          <label style={styles.ackCheckboxLabel}>
            <input
              type="checkbox"
              checked={false}
              disabled={acking}
              onChange={() => onAcknowledge(note.id)}
              style={styles.ackCheckbox}
            />
            <span>{acking ? 'Marking…' : 'Mark as read'}</span>
          </label>
        ) : pending && !isOwner ? (
          <span style={styles.ackAwaiting}>Awaiting {ackLabel}</span>
        ) : (
          <span style={styles.ackRead}>
            ✓ {isOwner ? 'You read this' : `Read by ${ackLabel}`}
            {note.acknowledgedAt ? ` · ${formatNoteTimeShort(note.acknowledgedAt)}` : ''}
          </span>
        )}
      </div>
    </div>
  );
}

function PlaceholderRow({ text }) {
  return (
    <div style={styles.entry}>
      <div style={styles.metaCol} />
      <div style={{ ...styles.noteCol, ...styles.placeholderBody }}>
        {text}
      </div>
    </div>
  );
}

// Spec-driven design tokens for the aged-paper notebook look.
const PAPER_BG = '#FBF8EE';
const PAPER_BORDER = '0.5px solid #D9C7A2';
const RULED_GRADIENT = `repeating-linear-gradient(
  to bottom,
  transparent 0,
  transparent 31px,
  rgba(140, 105, 65, 0.16) 31px,
  rgba(140, 105, 65, 0.16) 32px
)`;
const PAPER_BG_POSITION = '0 16px';

const styles = {
  section: {
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    marginBottom: '0.5rem',
    color: 'var(--cp-text)',
  },

  notepad: {
    position: 'relative',
    background: PAPER_BG,
    border: PAPER_BORDER,
    borderRadius: '10px',
    padding: '16px 0',
    overflow: 'hidden',
    marginBottom: '12px',
  },
  scroll: {
    position: 'relative',
    // 288 = 16 (top pad) + 8 × 32 (rows) + 16 (bottom pad). The
    // 16px padding mirrors `inputRuled` below: it shifts entries to
    // start at y=16 inside this box, so each entry's 32px bottom
    // edge lands on the gradient's rule line at y=47-48, 79-80, …
    height: '288px',
    overflowY: 'auto',
    backgroundImage: RULED_GRADIENT,
    backgroundPosition: PAPER_BG_POSITION,
    padding: '16px 0',
    lineHeight: '32px',
    boxSizing: 'border-box',
  },

  marginLine: {
    position: 'absolute',
    left: '120px',
    top: 0,
    bottom: 0,
    width: '1px',
    background: '#B85042',
    opacity: 0.55,
    pointerEvents: 'none',
    zIndex: 2,
  },
  coffeeStain: {
    position: 'absolute',
    right: '36px',
    bottom: '14px',
    width: '110px',
    height: 'auto',
    mixBlendMode: 'multiply',
    opacity: 0.85,
    pointerEvents: 'none',
    userSelect: 'none',
    zIndex: 1,
  },

  // Rows grow with their content, but always to a whole multiple of the
  // 32px ruled-line rhythm: the note text is the tallest flex child and its
  // line-height is exactly one rule, with no vertical padding, border, or
  // margin anywhere on the row — so wrapped lines all land on rule lines.
  entry: {
    display: 'flex',
    alignItems: 'stretch',
    minHeight: `${LINE_PX}px`,
    padding: '0 24px',
    boxSizing: 'border-box',
    margin: 0,
  },
  // Fixed to one ruled line so the date/time/author block stays anchored on
  // (and centred within) the FIRST line of a wrapped note, instead of
  // floating to the middle of a taller row.
  metaCol: {
    width: '88px',
    height: `${LINE_PX}px`,
    flexShrink: 0,
    paddingRight: '8px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    fontFamily: 'var(--font-ui)',
  },
  metaInner: { textAlign: 'left' },
  metaTop: {
    fontSize: '11px',
    lineHeight: '13px',
    fontWeight: 500,
    color: 'rgba(60, 50, 30, 0.92)',
  },
  metaBottom: {
    fontSize: '11px',
    lineHeight: '13px',
    fontWeight: 400,
    color: 'rgba(60, 50, 30, 0.7)',
    marginTop: '1px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '88px',
  },

  noteCol: {
    flex: 1,
    paddingLeft: '12px',
    fontFamily: 'var(--font-handwritten)',
    fontSize: '20px',
    // Line-height equal to one ruled line lets Caveat's own font metrics
    // place each baseline correctly within its 32px line box, and makes a
    // wrapped note land every line on a rule — the row simply grows by
    // whole 32px lines. `pre-wrap` preserves Shift+Enter hard breaks;
    // `break-word` wraps pasted URLs / long unbroken references.
    lineHeight: `${LINE_PX}px`,
    color: '#2c2418',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    minWidth: 0,
  },
  // Wraps the single-line input in the add-note row. The input itself
  // is width:100% and line-height:32px, so no flex centring is needed.
  noteColInput: {
    flex: 1,
    paddingLeft: '12px',
    fontFamily: 'var(--font-handwritten)',
    fontSize: '20px',
    lineHeight: '32px',
    color: '#2c2418',
    minWidth: 0,
  },

  // Wrapping (pre-wrap / break-word) is inherited from noteCol.
  bodyHuman: {},
  bodySystem: {
    fontStyle: 'italic',
    color: 'rgba(44, 36, 24, 0.7)',
  },
  placeholderBody: {
    fontStyle: 'italic',
    color: 'rgba(44, 36, 24, 0.5)',
  },

  // Acknowledgement (CLAUDE.md §31) — amber tokens kept distinct from the
  // app's red (urgent/overdue) and teal/blue (status) so they never collide.
  ackWrap: {
    position: 'relative',
  },
  ackWrapPending: {
    background: 'var(--cp-ack-amber-soft)',            // amber soft
    borderLeft: '4px solid var(--cp-ack-amber)',  // amber accent
  },
  // Exactly one ruled line tall (no vertical padding), so an
  // acknowledgement-bearing note still occupies a whole multiple of 32px
  // and the rows below it stay on the rule lines.
  ackStatusRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '0 24px',
    height: `${LINE_PX}px`,
    boxSizing: 'border-box',
  },
  ackCheckboxLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontFamily: 'var(--font-ui)',
    fontSize: '12px',
    fontWeight: 600,
    color: '#7c5a12',                 // amber text
    cursor: 'pointer',
  },
  ackCheckbox: {
    width: '14px',
    height: '14px',
    accentColor: 'var(--cp-ack-amber)',
    cursor: 'pointer',
  },
  ackAwaiting: {
    fontFamily: 'var(--font-ui)',
    fontSize: '11px',
    fontWeight: 600,
    fontStyle: 'italic',
    color: '#7c5a12',                 // amber text
  },
  ackRead: {
    fontFamily: 'var(--font-ui)',
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--cp-read-green)',                 // read / green
  },

  inputPanel: {
    position: 'relative',
    background: PAPER_BG,
    border: PAPER_BORDER,
    borderRadius: '10px',
    overflow: 'hidden',
    marginBottom: '8px',
  },
  // Inner ruled child — mirrors the role of `scroll` inside `notepad`.
  // Carries the gradient and the 16px top/bottom breathing room so rule
  // lines anchor consistently with the entries panel above.
  inputRuled: {
    position: 'relative',
    backgroundImage: RULED_GRADIENT,
    backgroundPosition: PAPER_BG_POSITION,
    padding: '16px 0',
  },
  inputRow: {
    display: 'flex',
    minHeight: '32px',
    padding: '0 24px',
    position: 'relative',
  },
  newEntryLabel: {
    fontSize: '11px',
    color: 'rgba(60, 50, 30, 0.4)',
    fontFamily: 'var(--font-ui)',
  },
  textarea: {
    width: '100%',
    fontFamily: 'var(--font-handwritten)',
    fontSize: '20px',
    // Same one-rule line-height as noteCol — typed lines land on the ruled
    // lines exactly like saved entries. Height is driven by the auto-grow
    // layout effect in whole 32px steps (this is just the starting height);
    // resize:none keeps the handle away so height stays under program
    // control, and the zero padding/border keep scrollHeight an exact
    // multiple of the line height.
    lineHeight: `${LINE_PX}px`,
    color: '#2c2418',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    padding: 0,
    margin: 0,
    height: `${LINE_PX}px`,
    display: 'block',
    resize: 'none',
    overflowY: 'hidden',
    overflowWrap: 'break-word',
    boxSizing: 'border-box',
  },

  inputFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 4px',
  },
  hint: {
    fontFamily: 'var(--font-ui)',
    fontSize: '0.72rem',
    color: 'var(--cp-text-muted)',
  },
  footerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
  },
  counter: {
    fontFamily: 'var(--font-ui)',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  addBtn: {
    padding: '0.35rem 0.9rem',
    fontSize: '0.8rem',
  },
  error: {
    color: 'var(--cp-danger)',
    fontFamily: 'var(--font-ui)',
    fontSize: '0.75rem',
    marginTop: '0.4rem',
  },
};
